import { useState, useCallback, useEffect, useMemo } from 'react'
import { UserPermissionsSingle } from '../../../../../baseComponents/CreateRoutering/types'
import { getNextOrder } from '../../../../../baseComponents/TwinTable/functions'
import { InputTableURLsTable } from '../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { DefaultOrder, TableConfiguration } from '../../../../../baseComponents/TwinTable/types'
import { twinFetchPostJSON } from '../../../../../utils/globals/data'
import { OrderData } from '../types'
import { buildOrderFieldsOrderLayout, groupTableDataByVariantParent, groupTableDataByAddonParent, cleanTableDataForOriginalTable, parseOrderTags, addErrorsOfTableDataInHeader, addInventPositionToOrderData, parseScalesOfOrderData } from './functions'
import { ExtraParseOrderDataFnc } from './types'
import useInputTableFilterData from '../../../../../utils/hooks/orders/useInputTableFilterData'
import { permissionCheck } from '../../../../../baseComponents/PermissionChecker/function'
import { PermissionThreeKeys } from '@teinor/erp/permissions'
import { getUserPermissions } from '../../../../../utils/reducers/getters'
import { TwinDictionary } from '../../../../../utils/globals/dictionary'
import { OrderLayoutTableFilter } from '../OrderTable/useOrderLayoutTableLogic/types'

interface OrderScreenLogicProps {
    inputTableURLs: InputTableURLsTable
    extraParseFunctionData?: ExtraParseOrderDataFnc
    userPermissions: PermissionThreeKeys
    defaultOrder?: DefaultOrder
    skipAddRow?: (result: TwinDictionary) => boolean
    extractWhereFormCompleteDataFilters?: (filters?: OrderLayoutTableFilter, defaultSpecificPriceRuleId?: number) => TwinDictionary
    defaultSpecificPriceRuleId?: number
}

const useOrderScreenLogic = ({ inputTableURLs, extraParseFunctionData, defaultOrder, userPermissions, skipAddRow, extractWhereFormCompleteDataFilters, defaultSpecificPriceRuleId }: OrderScreenLogicProps) => {
    const myUserPermissions = useMemo<UserPermissionsSingle>(() => getUserPermissions(userPermissions) || { n: 1, permission: 'read'}, [userPermissions])
    const [completeData, setCompleteData] = useState<OrderData | null>(null)
    const { parseTableData } = useInputTableFilterData({setCompleteData})
    const [tableConfiguration, setTableConfiguration] = useState<TableConfiguration>({
        generalSearchbar: '',
        order: {},
        offset: 0,
        singleFieldsSearch: {},
        petitionNumber: 0
    })

    const changeOrder = useCallback((fieldName: string) => {
        setTableConfiguration((oldTableConfiguration) => {
            const copyOld = JSON.parse(JSON.stringify(oldTableConfiguration))
            const newOrder = getNextOrder(fieldName, copyOld.order, defaultOrder?.order || 'desc')
            return {
                ...copyOld,
                offset: 0, 
                order: {...newOrder}
            }
        })
    }, [setTableConfiguration, defaultOrder?.order])

    const changeSingleField = useCallback((fieldName: string, value: string | string[]) => {
        setTableConfiguration((oldTableConfiguration) => {
            const copyOld = JSON.parse(JSON.stringify(oldTableConfiguration))
            return {
                ...copyOld,
                offset: 0,
                singleFieldsSearch: {
                    ...copyOld.singleFieldsSearch,
                    [fieldName]: value
                }
            }
        })
    }, [setTableConfiguration])
    
    const myTableConfigurationStr = JSON.stringify(tableConfiguration)
    const getDataStr = JSON.stringify(inputTableURLs.getData)
    const myExtraWhere = JSON.stringify(extractWhereFormCompleteDataFilters?.(completeData?.filters, defaultSpecificPriceRuleId) || {})
    const getCompleteData = useCallback(async () => {
        const myTableConfigurationParsed = JSON.parse(myTableConfigurationStr)
        const myExtraWhereParsed = JSON.parse(myExtraWhere)
        const getDataParsed = JSON.parse(getDataStr)
        const order = buildOrderFieldsOrderLayout(myTableConfigurationParsed.order)
        let result = await twinFetchPostJSON(getDataParsed[0], { ...getDataParsed[1], ...myExtraWhereParsed, includeRelations: true, order })
        result.originalTableData = cleanTableDataForOriginalTable(result.tableData)
        result.tableData = groupTableDataByVariantParent(result.tableData)
        result.tableData = groupTableDataByAddonParent(result.tableData)
        result.header = parseOrderTags(result.header)
        result = addErrorsOfTableDataInHeader(result)
        result = addInventPositionToOrderData(result)
        const mySkipAddRow = skipAddRow?.(result) ?? false
        if (!mySkipAddRow && result.header.status === 0 && !(!permissionCheck(myUserPermissions, 'update') || result?.header.hidePrices || result?.header.notModificable)) {
            result.tableData.push({ type: 'addButton', id: 'addButton' })
        }
        result = parseScalesOfOrderData(result)
        if (extraParseFunctionData){
            result = await extraParseFunctionData(result)
        }
        let processedTableLength = 0
        result.dictPosTableData = {}
        for (const key in result.tableData) {
            const element = result.tableData[key]
            if (element.processed) {
                processedTableLength++
            }
            const myParsedKey = parseInt(key)
            element.absoluteIndex = myParsedKey
            result.dictPosTableData[element.id] = myParsedKey
        }
        result.processedTableLength = processedTableLength
        return parseTableData((old) => old, result)
    }, [getDataStr, myTableConfigurationStr, extraParseFunctionData, parseTableData, myUserPermissions, skipAddRow, myExtraWhere])
    
    useEffect(() => {
        getCompleteData()
    }, [getCompleteData])

    return { completeData, setCompleteData, getCompleteData, tableConfiguration, setTableConfiguration, headerChanges: { changeOrder, changeSingleField }}
}

export default useOrderScreenLogic